/**
 * Source Profiles Management Panel
 * Full source profile editing: timing, tone, qualification, booking strategy
 */

import { useState } from "react";
import { usePipelineIntelligence } from "@/data/pipelineIntelligence/context";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  Settings, Clock, MessageSquare, Brain, Shield, Zap, Target,
  ChevronRight, TrendingUp, DollarSign, Phone,
} from "lucide-react";
import type { SourceProfile, AIMode, AIToneProfile } from "@/data/pipelineIntelligence/types";

const URGENCY_COLORS: Record<string, string> = {
  immediate: "bg-destructive/10 text-destructive border-destructive/20",
  high: "bg-warning/10 text-warning border-warning/20",
  normal: "bg-primary/10 text-primary border-primary/20",
  low: "bg-muted text-muted-foreground border-border",
};

const TONE_LABELS: Record<AIToneProfile, string> = {
  conservative: "Conservative",
  balanced: "Balanced",
  aggressive: "Aggressive",
  premium_consultative: "Premium Consultative",
};

const MODE_LABELS: Record<AIMode, string> = {
  disabled: "Disabled",
  suggest_only: "Suggest Only",
  auto_with_approval: "Auto + Approval",
  auto_send: "Auto Send",
};

const FUNNEL_LABELS: Record<string, string> = {
  high_intent: "High Intent",
  retargeting: "Retargeting",
  financing_offer: "Financing Offer",
  referral: "Referral",
  cold_reactivation: "Cold Reactivation",
  quote_followup: "Quote Follow-up",
  winback: "Winback",
  general: "General",
};

export function SourceProfilesPanel() {
  const { sourceProfiles, updateSourceProfile, performanceMetrics } = usePipelineIntelligence();
  const [editingProfile, setEditingProfile] = useState<SourceProfile | null>(null);
  const [showEdit, setShowEdit] = useState(false);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold">Lead Source Profiles</h2>
          <p className="text-[10px] text-muted-foreground mt-0.5">
            Define how leads should be handled: tone, urgency, qualification, and response window
          </p>
        </div>
        <Badge variant="secondary" className="text-[10px]">
          {sourceProfiles.filter(s => s.enabled).length}/{sourceProfiles.length} active
        </Badge>
      </div>

      <div className="grid gap-3">
        {sourceProfiles.map(profile => {
          const metrics = performanceMetrics.find(m => m.sourceId === profile.id);
          return (
            <SourceProfileCard
              key={profile.id}
              profile={profile}
              metrics={metrics}
              onEdit={() => { setEditingProfile({ ...profile }); setShowEdit(true); }}
              onToggle={(enabled) => {
                updateSourceProfile(profile.id, { enabled });
                toast.success(`${profile.sourceName} ${enabled ? "enabled" : "disabled"}`);
              }}
            />
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {editingProfile && (
            <SourceProfileEditor
              profile={editingProfile}
              onChange={setEditingProfile}
              onSave={() => {
                updateSourceProfile(editingProfile.id, editingProfile);
                setShowEdit(false);
                toast.success(`${editingProfile.sourceName} profile updated`);
              }}
              onCancel={() => setShowEdit(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SourceProfileCard({
  profile, metrics, onEdit, onToggle,
}: {
  profile: SourceProfile;
  metrics?: { totalLeads: number; booked: number; bookingRate: number; revenue: number; avgResponseTimeMinutes: number; roi: number; leadCost: number };
  onEdit: () => void;
  onToggle: (enabled: boolean) => void;
}) {
  const isHittingWindow = metrics && metrics.avgResponseTimeMinutes <= profile.responseWindowMinutes;

  return (
    <Card className={cn("shadow-none transition-all", !profile.enabled && "opacity-50")}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <Switch checked={profile.enabled} onCheckedChange={onToggle} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold">{profile.sourceName}</span>
              <Badge variant="outline" className={cn("text-[9px] h-4", URGENCY_COLORS[profile.responseUrgency])}>
                {profile.responseUrgency}
              </Badge>
              <Badge variant="outline" className="text-[9px] h-4">
                {FUNNEL_LABELS[profile.defaultFunnel] || profile.defaultFunnel}
              </Badge>
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">{profile.notes}</p>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-xs gap-1 shrink-0" onClick={onEdit}>
            <Settings className="h-3 w-3" /> Configure
          </Button>
        </div>

        {/* Quick Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3 pt-3 border-t border-border/30">
          <MiniStat
            icon={Clock}
            label="Response Window"
            value={profile.responseWindowMinutes === 0 ? "Instant" : `${profile.responseWindowMinutes}m`}
          />
          <MiniStat
            icon={MessageSquare}
            label="Max Steps"
            value={`${profile.maxSequenceSteps}`}
          />
          <MiniStat
            icon={Brain}
            label="AI Mode"
            value={MODE_LABELS[profile.aiMode]}
          />
          <MiniStat
            icon={Target}
            label="Quality"
            value={`${profile.qualityScore}/100`}
          />
          <MiniStat
            icon={DollarSign}
            label="Price Sensitivity"
            value={`${profile.priceSensitivity}%`}
          />
          <MiniStat
            icon={isHittingWindow ? TrendingUp : Clock}
            label="Actual Avg"
            value={metrics ? `${metrics.avgResponseTimeMinutes.toFixed(1)}m` : "—"}
            highlight={isHittingWindow ? "positive" : metrics ? "warning" : undefined}
          />
        </div>

        {/* Quality Bars */}
        <div className="grid grid-cols-3 gap-3 mt-3">
          <QualityBar label="Quality Score" value={profile.qualityScore} />
          <QualityBar label="Intent Score" value={profile.intentScore} />
          <QualityBar label="Price Sensitivity" value={profile.priceSensitivity} inverted />
        </div>

        {/* Revenue Impact */}
        {metrics && (
          <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-border/30">
            <MiniStat icon={Target} label="Leads" value={`${metrics.totalLeads}`} />
            <MiniStat icon={TrendingUp} label="Booking Rate" value={`${metrics.bookingRate.toFixed(1)}%`}
              highlight={metrics.bookingRate >= 40 ? "positive" : metrics.bookingRate >= 20 ? undefined : "warning"} />
            <MiniStat icon={DollarSign} label="Revenue" value={`$${(metrics.revenue / 1000).toFixed(0)}K`} highlight="positive" />
            <MiniStat icon={TrendingUp} label="ROI" value={`${isFinite(metrics.roi) ? metrics.roi.toFixed(1) : "∞"}x`}
              highlight={metrics.roi > 10 ? "positive" : "warning"} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value, highlight }: {
  icon: React.ElementType;
  label: string;
  value: string;
  highlight?: "positive" | "warning";
}) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={cn("h-3 w-3 shrink-0",
        highlight === "positive" ? "text-success" : highlight === "warning" ? "text-warning" : "text-muted-foreground"
      )} />
      <div className="min-w-0">
        <p className="text-[9px] text-muted-foreground leading-none">{label}</p>
        <p className={cn("text-[11px] font-medium leading-tight",
          highlight === "positive" ? "text-success" : highlight === "warning" ? "text-warning" : ""
        )}>{value}</p>
      </div>
    </div>
  );
}

function QualityBar({ label, value, inverted }: { label: string; value: number; inverted?: boolean }) {
  const color = inverted
    ? (value >= 70 ? "text-destructive" : value >= 40 ? "text-warning" : "text-success")
    : (value >= 70 ? "text-success" : value >= 40 ? "text-warning" : "text-destructive");

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[9px] text-muted-foreground">{label}</span>
        <span className={cn("text-[10px] font-bold", color)}>{value}</span>
      </div>
      <Progress value={value} className="h-1" />
    </div>
  );
}

function SourceProfileEditor({
  profile, onChange, onSave, onCancel,
}: {
  profile: SourceProfile;
  onChange: (p: SourceProfile) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const update = (updates: Partial<SourceProfile>) => onChange({ ...profile, ...updates });

  return (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Settings className="h-4 w-4" />
          {profile.sourceName} — Source Profile
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-5">
        {/* Timing & Response */}
        <section>
          <SectionLabel icon={Clock} label="Timing Controls" />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Response Window (minutes)">
              <Input
                type="number" min={0} max={120}
                value={profile.responseWindowMinutes}
                onChange={e => update({ responseWindowMinutes: parseInt(e.target.value) || 0 })}
                className="h-8 text-xs"
              />
            </Field>
            <Field label="Response Urgency">
              <Select value={profile.responseUrgency} onValueChange={(v: any) => update({ responseUrgency: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="immediate">Immediate</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                  <SelectItem value="low">Low</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Max Sequence Steps">
              <Input
                type="number" min={1} max={20}
                value={profile.maxSequenceSteps}
                onChange={e => update({ maxSequenceSteps: parseInt(e.target.value) || 3 })}
                className="h-8 text-xs"
              />
            </Field>
            <Field label="Business Hours Only">
              <div className="flex items-center gap-2 h-8">
                <Switch
                  checked={profile.businessHoursOnly}
                  onCheckedChange={v => update({ businessHoursOnly: v })}
                />
                <span className="text-xs text-muted-foreground">
                  {profile.businessHoursOnly ? "Business hours only" : "24/7 response"}
                </span>
              </div>
            </Field>
          </div>
        </section>

        <Separator />

        {/* Qualification & Scoring */}
        <section>
          <SectionLabel icon={Target} label="Qualification Logic" />
          <div className="grid grid-cols-3 gap-3 mt-2">
            <Field label="Quality Score">
              <Input
                type="number" min={0} max={100}
                value={profile.qualityScore}
                onChange={e => update({ qualityScore: parseInt(e.target.value) || 0 })}
                className="h-8 text-xs"
              />
            </Field>
            <Field label="Intent Score">
              <Input
                type="number" min={0} max={100}
                value={profile.intentScore}
                onChange={e => update({ intentScore: parseInt(e.target.value) || 0 })}
                className="h-8 text-xs"
              />
            </Field>
            <Field label="Price Sensitivity">
              <Input
                type="number" min={0} max={100}
                value={profile.priceSensitivity}
                onChange={e => update({ priceSensitivity: parseInt(e.target.value) || 0 })}
                className="h-8 text-xs"
              />
            </Field>
          </div>
          <Field label="Default Funnel" className="mt-3">
            <Select value={profile.defaultFunnel} onValueChange={(v: any) => update({ defaultFunnel: v })}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(FUNNEL_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </section>

        <Separator />

        {/* Communication Tone */}
        <section>
          <SectionLabel icon={MessageSquare} label="Communication Strategy" />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <Field label="Communication Tone">
              <Select value={profile.aiTone} onValueChange={(v: any) => update({ aiTone: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TONE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="AI Mode">
              <Select value={profile.aiMode} onValueChange={(v: any) => update({ aiMode: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(MODE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        </section>

        <Separator />

        {/* Notes */}
        <section>
          <SectionLabel icon={Shield} label="Operator Notes" />
          <Textarea
            value={profile.notes}
            onChange={e => update({ notes: e.target.value })}
            className="mt-2 text-xs min-h-[60px]"
            placeholder="Strategy notes, booking reminders..."
          />
        </section>
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel} className="text-xs">Cancel</Button>
        <Button onClick={onSave} className="text-xs">Save Profile</Button>
      </DialogFooter>
    </>
  );
}

function SectionLabel({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
    </div>
  );
}

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={className}>
      <label className="text-[10px] text-muted-foreground block mb-1">{label}</label>
      {children}
    </div>
  );
}
